import * as Sentry from '@sentry/react';
import { EnvironmentService } from '../../../../../environment/environment-service';
import { Hasher } from '../../../../api/hasher';
import {
  DefaultLocalStorageService,
  JsonLocalStorageService,
} from '../../../../api/local-storage-service';
import { HasDependencies, hashString, Role } from '../../../../api/types';
import { FirebaseUser } from '../../../../api/user/types';
import { Domains } from '../../../../constants';
import { ErrorTypes } from '../types';
import { getSentryPanalytTags } from '../utils';
import { ErrorTracker } from './ErrorTracker';

export class AuthedErrorTracker implements ErrorTracker, HasDependencies {
  constructor(
    readonly environmentService: EnvironmentService,
    readonly currentUser: FirebaseUser | null,
    readonly localStorageCurrentUserService: JsonLocalStorageService<FirebaseUser>,
    readonly localStorageDomainService: DefaultLocalStorageService<Domains>,
    readonly localStorageExecutorRoleService: JsonLocalStorageService<Role>
  ) {}

  public getDeps = (): (string | undefined)[] => [
    hashString([
      ...this.environmentService.getDeps(),
      Hasher.hash(this.currentUser),
    ]),
  ];

  public trackError = (params: {
    error: Error;
    errorType?: ErrorTypes;
    contexts?: Record<string, any>;
    tags?: Record<string, any>;
    extras?: { key: string; value: Record<string, any> };
  }) => {
    const { error, errorType, contexts, tags, extras } = params;
    const subDomain = this.environmentService.getSubDomain();
    const currentUserFromLocalStorage =
      this.localStorageCurrentUserService.get();
    const currentUser = this.currentUser ?? currentUserFromLocalStorage;
    const executorRoleFromLocalStorage =
      this.localStorageExecutorRoleService.get();
    const domainFromLocalStorage = this.localStorageDomainService.get();

    if (Sentry) {
      Sentry.withScope((scope) => {
        scope.setUser({
          ...scope.getUser(),
          email: currentUser?.email ?? undefined,
        });
        const panalytTags = getSentryPanalytTags({
          subDomain,
          domain: null,
          domainFromLocalStorage,
          executorRole: null,
          executorRoleFromLocalStorage,
          simulatedRole: null,
          currentUser: this.currentUser,
          currentUserFromLocalStorage,
          errorType,
        });
        scope.setTags({ ...(tags ?? {}), ...panalytTags });
        if (extras) {
          scope.setExtra(extras.key, extras.value);
        }
        Sentry.captureException(error, contexts);
        Sentry.setExtras({ customData: undefined }); // Reset extras so that they are not used within the next request
      });
    }
  };
}
